/**
 * 어드민 채널 관리(/admin/channels) 대기열 계산 유틸.
 *
 * 배경 — `channel.registered_at` 은 text 컬럼이고 값 형식이 섞여 있다 (2026-09-06 라이브 실측):
 *   · `2026-09-05T05:46:21.068Z`      ISO — 현재 등록 경로가 쓰는 형식 (1,470건)
 *   · `Jun 29, 2025 11:08 pm`          구 bubble.io 형식 (2건)
 *   · null                             (6건)
 * 텍스트 정렬은 'J' > '2' 라서 구 형식 2건이 최신 등록보다 위로 올라온다.
 * **그래서 이 컬럼으로 DB 정렬(order)을 하지 않는다.** 대기열은 여기서 파싱해 정렬한다.
 */
import { YPP_STATE, yppAdminLabel, yppState } from "./channelYpp";

/** 섞여 있는 날짜 표기를 Date 로. 못 읽으면 null. */
export function parseChannelDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (!raw) return null;

  let text = raw;
  if (/^\d{4}-\d{2}-\d{2}[ T]/.test(text)) {
    // Postgres timestamptz 표기(`2026-04-25 01:19:36.314701+09`)를 Date 가 읽는 형태로 맞춘다.
    // 공백 구분 · 마이크로초 6자리 · 분이 빠진 오프셋 — 셋 다 Date 가 그대로는 못 읽는다.
    text = text.replace(" ", "T");
    text = text.replace(/\.(\d{3})\d+/, ".$1");
    text = text.replace(/([+-]\d{2})$/, "$1:00");
  } else {
    // `Jun 29, 2025 11:08 pm` — 소문자 am/pm 을 못 읽는 엔진이 있다
    text = text.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** 등록(재등록 포함) 시각. registered_at 을 못 읽으면 created_date 로 내려간다. */
export function getRegisteredDate(channel) {
  if (!channel) return null;
  return (
    parseChannelDate(channel.registered_at) ||
    parseChannelDate(channel.created_date)
  );
}

/** 등록 후 지난 일수. 날짜를 못 읽으면 null. */
export function waitingDays(channel, now = Date.now()) {
  const at = getRegisteredDate(channel);
  if (!at) return null;
  const diff = now - at.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / 86400000);
}

/**
 * 경과일 심각도 4단계. 화면의 뱃지 색이 이 값을 따른다.
 * 승인 대기 77건 중 69건이 30일 초과였다(2026-09-06) — `stale` 이 눈에 띄어야 한다.
 */
export function ageBucket(days) {
  if (days === null || days === undefined) return "unknown";
  if (days <= 1) return "fresh";
  if (days <= 7) return "week";
  if (days <= 30) return "month";
  return "stale";
}

export const AGE_BADGE = {
  fresh: { colorScheme: "brand", label: (d) => (d <= 0 ? "오늘 등록" : `${d}일 대기`) },
  week: { colorScheme: "gray", label: (d) => `${d}일 대기` },
  month: { colorScheme: "orange", label: (d) => `${d}일 대기` },
  stale: { colorScheme: "red", label: (d) => `${d}일 대기` },
  unknown: { colorScheme: "gray", label: () => "등록일 없음" },
};

/**
 * 대기열 정렬. 기본은 `oldest`(오래 기다린 순) — 밀린 건부터 지우기 위해서다.
 * 날짜를 못 읽는 건은 어느 방향이든 뒤로 보낸다.
 */
export function sortQueue(channels, mode = "oldest") {
  const list = Array.isArray(channels) ? [...channels] : [];
  const key = (c) => {
    const at = getRegisteredDate(c);
    return at ? at.getTime() : null;
  };
  return list.sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka === null && kb === null) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    return mode === "newest" ? kb - ka : ka - kb;
  });
}

/**
 * 아직 처리되지 않은 삭제 요청.
 * `channel_delete_request` 에 상태 컬럼이 없어(88건이 영구 누적) **채널이 아직 살아 있는지**로 판정한다.
 * 채널을 삭제하면 그 요청은 이 목록에서 자동으로 빠진다.
 */
export function pendingDeleteRequests(requests, aliveChannelIds) {
  const alive =
    aliveChannelIds instanceof Set ? aliveChannelIds : new Set(aliveChannelIds || []);
  return (requests || []).filter((r) => r && alive.has(r.channel_unique_id));
}

/* ── 표 목록 (2026-09-06, 회원 관리 목록과 같은 양식) ─────────────────────
 * 기획: docs/ops/admin-users-list-columns-plan.md 의 원칙을 채널에 그대로 적용.
 * 전부 페이지 안(최대 20건) 계산이며 서버 정렬·필터가 아니다.
 */

/** "58,600" · "58600" · 58600 → 58600. 못 읽으면 0. subscriber_count·views 는 text 컬럼이다. */
export function toCount(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/**
 * 게이트 점 4개 — 사용자 인증 · 뷰어 수락 · 통화 확인 · YPP. 표에서는 칩 대신 점으로 그린다.
 * 판정은 channelViews.gateChips 와 같다. on = 끝남, half = 자기신고만, off = 아직.
 */
export function gateDots(channel) {
  const c = channel || {};
  const verify = c.status === "Approved" ? "on" : "off";
  const viewer = c.viewer_accepted_at ? "on" : c.viewer_invited_at ? "half" : "off";
  const currency = c.currency_confirmed_at ? "on" : c.currency_self_reported_at ? "half" : "off";
  // YPP 도 통화처럼 자기신고(ypp_self_reported_at) 를 half 로 — gateChips 의 「YPP 확인 요청」 과 같은 판정 (dev 채널 한도 2차)
  // 2026-10-03: 등록할 때 본인이 고른 「승인」(ypp_pending_since)도 half — 확인된 승인만 on (lib channelYpp)
  const yState = yppState(c);
  const ypp = yState === YPP_STATE.VERIFIED ? "on" : yState === YPP_STATE.NONE ? "off" : "half";
  const label = [
    verify === "on" ? "사용자 인증 완료" : "사용자 인증 전",
    viewer === "on" ? "뷰어 초대 수락" : viewer === "half" ? "뷰어 초대 보냄" : "뷰어 초대 미확인",
    currency === "on" ? "원화(KRW) 확인" : currency === "half" ? "원화(KRW) 자기신고" : "원화(KRW) 미확인",
    yppAdminLabel(c),
  ].join(" · ");
  return {
    dots: [
      { key: "verify", state: verify },
      { key: "viewer", state: viewer },
      { key: "currency", state: currency },
      { key: "ypp", state: ypp },
    ],
    label,
  };
}

/** 승인 칸 — 삭제됨 > 승인됨 > 대기(경과일). */
export function approvalCell(channel, now = Date.now()) {
  if (channel?.deleted_at) return { key: "deleted", label: "삭제됨", days: null };
  if (channel?.approved_yn) return { key: "approved", label: "승인됨", days: null };
  const days = waitingDays(channel, now);
  const style = AGE_BADGE[ageBucket(days)];
  return { key: "pending", label: style.label(days), days, colorScheme: style.colorScheme };
}

/**
 * 표 열 머리 정렬(페이지 안). key: subscribers · views · wait(등록일) · deleted(삭제일).
 * 값이 없는 행은 방향과 무관하게 뒤로, 같은 값은 원래 순서 유지 — 회원 목록 sortUsersByColumn 과 같은 규칙.
 */
export function sortChannelsByColumn(channels, sort) {
  const list = Array.isArray(channels) ? [...channels] : [];
  if (!sort || !sort.key) return list;
  const dir = sort.dir === "asc" ? 1 : -1;
  const valueOf = (c) => {
    switch (sort.key) {
      case "subscribers":
        return toCount(c?.subscriber_count);
      case "views":
        return toCount(c?.views);
      case "wait": {
        const at = getRegisteredDate(c);
        return at ? at.getTime() : null;
      }
      case "deleted": {
        const at = parseChannelDate(c?.deleted_at);
        return at ? at.getTime() : null;
      }
      default:
        return null;
    }
  };
  return list
    .map((c, i) => ({ c, i, v: valueOf(c) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      if (a.v === b.v) return a.i - b.i;
      return (a.v - b.v) * dir;
    })
    .map((x) => x.c);
}
